import { Brand } from '../brand';
import { Practice } from '../practice/practice';
import {
  IAIReceptionistSettings,
  IOvieSettingsBookingCatalogPatch,
  IOvieSettingsBookingCatalogValue,
  IOvieSettingsBookingPoliciesPatch,
  IOvieSettingsBookingPoliciesValue,
  IOvieSettingsCallHandlingPatch,
  IOvieSettingsCallHandlingValue,
  IOvieSettingsCommunicationsPatch,
  IOvieSettingsCommunicationsValue,
  IOvieSettingsGreetingPatch,
  IOvieSettingsGreetingValue,
  IOvieSettingsMessagesPatch,
  IOvieSettingsMessagesValue,
  IOvieSettingsRoutingPatch,
  IOvieSettingsRoutingValue,
  IOvieSettingsTreatmentAlias,
  IOvieTreatmentAliasWire,
  IPractice,
  OvieHealthSettingsSection,
  SETTINGS_MAX_BOOKABLE_TREATMENT_IDS,
  SETTINGS_MAX_BOOKING_POLICIES,
  SETTINGS_MAX_TREATMENT_ALIAS_DESCRIPTION_LENGTH,
  SETTINGS_MAX_TREATMENT_ALIAS_KEYS,
  SETTINGS_MAX_TREATMENT_ALIAS_NAME_LENGTH,
  SETTINGS_TREATMENT_ALIAS_RESERVED_SEPARATOR,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  StringHelpers,
  WithRef,
  undeletedQuery,
} from '@principle-theorem/shared';
import { deriveKnowledgeBaseFromTemplates } from './knowledge-base-template-derivation';

export const INTEGRATED_SECTIONS = [
  OvieHealthSettingsSection.Greeting,
  OvieHealthSettingsSection.Messages,
  OvieHealthSettingsSection.CallHandling,
  OvieHealthSettingsSection.Routing,
  OvieHealthSettingsSection.Communications,
  OvieHealthSettingsSection.BookingCatalog,
  OvieHealthSettingsSection.BookingPolicies,
] as const;

export type IntegratedSection = (typeof INTEGRATED_SECTIONS)[number];

type SettingsJsonField =
  | 'greeting'
  | 'messages'
  | 'callHandling'
  | 'routing'
  | 'communications'
  | 'bookingCatalog'
  | 'bookingPolicies';

const SECTION_TO_FIELD: Record<IntegratedSection, SettingsJsonField> = {
  [OvieHealthSettingsSection.Greeting]: 'greeting',
  [OvieHealthSettingsSection.Messages]: 'messages',
  [OvieHealthSettingsSection.CallHandling]: 'callHandling',
  [OvieHealthSettingsSection.Routing]: 'routing',
  [OvieHealthSettingsSection.Communications]: 'communications',
  [OvieHealthSettingsSection.BookingCatalog]: 'bookingCatalog',
  [OvieHealthSettingsSection.BookingPolicies]: 'bookingPolicies',
};

export class OvieSettingsValidationError extends Error {
  constructor(
    public readonly section: OvieHealthSettingsSection,
    public readonly reason: string
  ) {
    super(`[${section}] ${reason}`);
    this.name = 'OvieSettingsValidationError';
  }
}

/**
 * Ovie PATCH is a partial merge: an omitted field is untouched; an explicit
 * `null` clears it. Emit a non-null value always; emit `null` only to clear a
 * previously-set field, never for a field that was already unset.
 */
function shouldEmit<V>(
  next: V | null,
  previous: V | null | undefined
): boolean {
  // eslint-disable-next-line no-null/no-null
  if (next !== null) {
    return true;
  }
  // eslint-disable-next-line no-null/no-null
  return previous !== null && previous !== undefined;
}

function buildGreetingPatch(
  value: IOvieSettingsGreetingValue,
  previous: Partial<IOvieSettingsGreetingValue> | undefined
): IOvieSettingsGreetingPatch {
  const patch: IOvieSettingsGreetingPatch = {};
  if (shouldEmit(value.startMessage, previous?.startMessage)) {
    patch.startMessage = value.startMessage;
  }
  if (shouldEmit(value.multilocationMessage, previous?.multilocationMessage)) {
    patch.multilocationMessage = value.multilocationMessage;
  }
  return patch;
}

function buildMessagesPatch(
  value: IOvieSettingsMessagesValue,
  previous: Partial<IOvieSettingsMessagesValue> | undefined
): IOvieSettingsMessagesPatch {
  const patch: IOvieSettingsMessagesPatch = {};
  if (shouldEmit(value.afterHoursMessage, previous?.afterHoursMessage)) {
    patch.afterHoursMessage = value.afterHoursMessage;
  }
  if (shouldEmit(value.publicHolidayNote, previous?.publicHolidayNote)) {
    patch.publicHolidayNote = value.publicHolidayNote;
  }
  if (shouldEmit(value.agentNotes, previous?.agentNotes)) {
    patch.agentNotes = value.agentNotes;
  }
  return patch;
}

function buildCallHandlingPatch(
  value: IOvieSettingsCallHandlingValue,
  previous: Partial<IOvieSettingsCallHandlingValue> | undefined
): IOvieSettingsCallHandlingPatch {
  const patch: IOvieSettingsCallHandlingPatch = {};
  if (
    shouldEmit(
      value.transferSuspensionMessage,
      previous?.transferSuspensionMessage
    )
  ) {
    patch.transferSuspensionMessage = value.transferSuspensionMessage;
  }
  if (
    shouldEmit(
      value.maxTransfersBeforeSuspension,
      previous?.maxTransfersBeforeSuspension
    )
  ) {
    patch.maxTransfersBeforeSuspension = value.maxTransfersBeforeSuspension;
  }
  if (
    shouldEmit(
      value.transferSuspensionMinutes,
      previous?.transferSuspensionMinutes
    )
  ) {
    patch.transferSuspensionMinutes = value.transferSuspensionMinutes;
  }
  if (shouldEmit(value.repeatCallerLimit, previous?.repeatCallerLimit)) {
    patch.repeatCallerLimit = value.repeatCallerLimit;
  }
  if (
    shouldEmit(
      value.repeatCallerWindowMinutes,
      previous?.repeatCallerWindowMinutes
    )
  ) {
    patch.repeatCallerWindowMinutes = value.repeatCallerWindowMinutes;
  }
  return patch;
}

function buildRoutingPatch(
  value: IOvieSettingsRoutingValue,
  previous: Partial<IOvieSettingsRoutingValue> | undefined
): IOvieSettingsRoutingPatch {
  const patch: IOvieSettingsRoutingPatch = {};
  if (shouldEmit(value.transferPhone, previous?.transferPhone)) {
    patch.transferPhone = value.transferPhone;
  }
  if (
    shouldEmit(value.afterhoursTransferPhone, previous?.afterhoursTransferPhone)
  ) {
    patch.afterhoursTransferPhone = value.afterhoursTransferPhone;
  }
  return patch;
}

function buildCommunicationsPatch(
  value: IOvieSettingsCommunicationsValue,
  previous: Partial<IOvieSettingsCommunicationsValue> | undefined
): IOvieSettingsCommunicationsPatch {
  const patch: IOvieSettingsCommunicationsPatch = {};
  if (shouldEmit(value.notificationEmail, previous?.notificationEmail)) {
    patch.notificationEmail = value.notificationEmail;
  }
  return patch;
}

// eslint-disable-next-line no-null/no-null
const ALIAS_DELETION = null;

/**
 * Ovie reserves the em dash sequence for composite alias names, so a
 * user-authored occurrence is downgraded to a plain hyphen rather than rejected.
 */
function sanitiseAliasName(name: string): string {
  const stripped = name
    .split(SETTINGS_TREATMENT_ALIAS_RESERVED_SEPARATOR)
    .join(' - ');
  const clamped = StringHelpers.clampText(
    stripped,
    SETTINGS_MAX_TREATMENT_ALIAS_NAME_LENGTH
  );
  return (clamped ?? '').trim();
}

function buildAliasWire(
  alias: IOvieSettingsTreatmentAlias
): IOvieTreatmentAliasWire | undefined {
  const description =
    StringHelpers.clampText(
      alias.description,
      SETTINGS_MAX_TREATMENT_ALIAS_DESCRIPTION_LENGTH
    ) ?? '';
  if (description.trim() === '') {
    return undefined;
  }
  const name = sanitiseAliasName(alias.name);
  return {
    description,
    ...(name !== '' ? { name } : {}),
  };
}

interface IBookingCatalogPatchOptions {
  bookableTreatmentIds: string[] | undefined;
  excludedTemplateIds: Set<string>;
  remoteAliasKeys?: Set<string>;
}

function buildBookingCatalogPatch(
  value: IOvieSettingsBookingCatalogValue,
  previous: Partial<IOvieSettingsBookingCatalogValue> | undefined,
  validTreatmentIds: Set<string>,
  options: IBookingCatalogPatchOptions
): IOvieSettingsBookingCatalogPatch {
  const { bookableTreatmentIds, excludedTemplateIds, remoteAliasKeys } =
    options;

  const emittedAliases = new Map<string, IOvieTreatmentAliasWire>();
  for (const alias of value.treatmentAliases) {
    if (
      !validTreatmentIds.has(alias.treatmentId) ||
      excludedTemplateIds.has(alias.treatmentId)
    ) {
      continue;
    }
    const wire = buildAliasWire(alias);
    if (wire) {
      emittedAliases.set(alias.treatmentId, wire);
    }
  }

  // Per-key merge: `null` deletes an alias previously sent but now removed,
  // description-cleared or excluded. Deleting a key Ovie never had is a no-op.
  const deletionKeys = new Set<string>();
  const addDeletion = (treatmentId: string): void => {
    if (!emittedAliases.has(treatmentId)) {
      deletionKeys.add(treatmentId);
    }
  };

  // A stored alias for a still-valid treatment that was not emitted has been
  // excluded from the knowledge base, so its live Ovie alias must go too.
  for (const alias of value.treatmentAliases) {
    if (
      validTreatmentIds.has(alias.treatmentId) &&
      alias.description.trim() !== ''
    ) {
      addDeletion(alias.treatmentId);
    }
  }
  for (const alias of previous?.treatmentAliases ?? []) {
    if (alias.description.trim() !== '') {
      addDeletion(alias.treatmentId);
    }
  }
  // Sweeping the keys Ovie currently holds is the only repair for an alias whose
  // original delete push never landed: it survives in neither list below.
  for (const treatmentId of remoteAliasKeys ?? []) {
    addDeletion(treatmentId);
  }

  // Ovie rejects an over-cap request outright, taking `bookableTreatmentIds`
  // down with it. Non-null entries win the available slots; deferred deletions
  // converge on a subsequent push.
  const deletionEntries = [...deletionKeys].map<
    [string, IOvieTreatmentAliasWire | null]
  >((treatmentId) => [treatmentId, ALIAS_DELETION]);
  const treatmentAliases = Object.fromEntries(
    [...emittedAliases.entries(), ...deletionEntries].slice(
      0,
      SETTINGS_MAX_TREATMENT_ALIAS_KEYS
    )
  );

  return {
    treatmentAliases,
    ...(bookableTreatmentIds ? { bookableTreatmentIds } : {}),
  };
}

function buildBookingPoliciesPatch(
  value: IOvieSettingsBookingPoliciesValue
): IOvieSettingsBookingPoliciesPatch {
  if (value.policies.length > SETTINGS_MAX_BOOKING_POLICIES) {
    throw new OvieSettingsValidationError(
      OvieHealthSettingsSection.BookingPolicies,
      `Policy count ${value.policies.length} exceeds maximum of ${SETTINGS_MAX_BOOKING_POLICIES}`
    );
  }
  return { policies: value.policies };
}

function parseSection<T>(
  jsonString: string | undefined,
  section: OvieHealthSettingsSection
): T {
  if (jsonString === undefined) {
    throw new Error(
      `No stored data for section '${section}' — sync the section before composing a patch`
    );
  }
  return JSON.parse(jsonString) as T;
}

function parseOptionalSection<T>(
  jsonString: string | undefined
): Partial<T> | undefined {
  if (jsonString === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(jsonString) as Partial<T>;
  } catch {
    return undefined;
  }
}

/**
 * Supplied by the caller for the booking catalog section only. An absent context
 * means zero exclusions and no bookable-id recompute, so a failed or missing
 * knowledge base read can never wipe the live catalog.
 */
export interface IBookingCatalogComposeContext {
  excludedTemplateIds: Set<string>;
  recomputeBookableTreatmentIds: boolean;
  remoteAliasKeys?: Set<string>;
}

async function deriveBookableTreatmentIds(
  practice: WithRef<IPractice>,
  excludedTemplateIds: Set<string>
): Promise<string[] | undefined> {
  const derivation = await deriveKnowledgeBaseFromTemplates({
    practice,
    brandRef: Practice.brandDoc(practice),
    excludedTemplateIds,
  });
  if (derivation.items.length === 0) {
    return undefined;
  }
  return derivation.items
    .filter((item) => item.isBookable)
    .map((item) => item.templateId)
    .slice(0, SETTINGS_MAX_BOOKABLE_TREATMENT_IDS);
}

export class AIReceptionistSettingsCompose {
  static async fetchValidTreatmentIds(
    practice: WithRef<IPractice>
  ): Promise<Set<string>> {
    const brandRef = Practice.brandDoc(practice);
    const templates = await Firestore.getDocs(
      undeletedQuery(Brand.treatmentTemplateCol({ ref: brandRef }))
    );
    return new Set(templates.map((template) => template.ref.id));
  }

  static async composeSectionPatch(
    section: OvieHealthSettingsSection,
    settings: IAIReceptionistSettings,
    practice: WithRef<IPractice>,
    previous?: IAIReceptionistSettings,
    context?: IBookingCatalogComposeContext
  ): Promise<Record<string, unknown>> {
    switch (section) {
      case OvieHealthSettingsSection.Greeting: {
        const value = parseSection<IOvieSettingsGreetingValue>(
          settings.greeting,
          section
        );
        const prev = parseOptionalSection<IOvieSettingsGreetingValue>(
          previous?.greeting
        );
        return buildGreetingPatch(value, prev) as Record<string, unknown>;
      }
      case OvieHealthSettingsSection.Messages: {
        const value = parseSection<IOvieSettingsMessagesValue>(
          settings.messages,
          section
        );
        const prev = parseOptionalSection<IOvieSettingsMessagesValue>(
          previous?.messages
        );
        return buildMessagesPatch(value, prev) as Record<string, unknown>;
      }
      case OvieHealthSettingsSection.CallHandling: {
        const value = parseSection<IOvieSettingsCallHandlingValue>(
          settings.callHandling,
          section
        );
        const prev = parseOptionalSection<IOvieSettingsCallHandlingValue>(
          previous?.callHandling
        );
        return buildCallHandlingPatch(value, prev) as Record<string, unknown>;
      }
      case OvieHealthSettingsSection.Routing: {
        const value = parseSection<IOvieSettingsRoutingValue>(
          settings.routing,
          section
        );
        const prev = parseOptionalSection<IOvieSettingsRoutingValue>(
          previous?.routing
        );
        return buildRoutingPatch(value, prev) as Record<string, unknown>;
      }
      case OvieHealthSettingsSection.Communications: {
        const value = parseSection<IOvieSettingsCommunicationsValue>(
          settings.communications,
          section
        );
        const prev = parseOptionalSection<IOvieSettingsCommunicationsValue>(
          previous?.communications
        );
        return buildCommunicationsPatch(value, prev) as Record<string, unknown>;
      }
      case OvieHealthSettingsSection.BookingCatalog: {
        // Bookable ids are derived rather than stored, so the section can be
        // composed before any alias has ever been saved.
        const value: IOvieSettingsBookingCatalogValue =
          settings.bookingCatalog === undefined
            ? { treatmentAliases: [] }
            : parseSection<IOvieSettingsBookingCatalogValue>(
                settings.bookingCatalog,
                section
              );
        const prev = parseOptionalSection<IOvieSettingsBookingCatalogValue>(
          previous?.bookingCatalog
        );
        const validIds =
          await AIReceptionistSettingsCompose.fetchValidTreatmentIds(practice);
        const excludedTemplateIds =
          context?.excludedTemplateIds ?? new Set<string>();
        const bookableTreatmentIds = context?.recomputeBookableTreatmentIds
          ? await deriveBookableTreatmentIds(practice, excludedTemplateIds)
          : undefined;
        return buildBookingCatalogPatch(value, prev, validIds, {
          bookableTreatmentIds,
          excludedTemplateIds,
          remoteAliasKeys: context?.remoteAliasKeys,
        }) as Record<string, unknown>;
      }
      case OvieHealthSettingsSection.BookingPolicies: {
        const value = parseSection<IOvieSettingsBookingPoliciesValue>(
          settings.bookingPolicies,
          section
        );
        return buildBookingPoliciesPatch(value) as Record<string, unknown>;
      }
      default: {
        throw new Error(
          `Section '${section}' is not an integrated section — composition is not supported`
        );
      }
    }
  }

  static changedSections(
    previous: IAIReceptionistSettings,
    next: IAIReceptionistSettings
  ): OvieHealthSettingsSection[] {
    return INTEGRATED_SECTIONS.filter((section) => {
      const field = SECTION_TO_FIELD[section];
      return previous[field] !== next[field];
    });
  }

  static settingsFieldForSection(
    section: IntegratedSection
  ): SettingsJsonField {
    return SECTION_TO_FIELD[section];
  }
}
